'use client'

import ImageWithFallback from '@/components/common/image-with-fallback'
import Loader from '@/components/common/loader'
import ChevronLeft from '@/components/icons/chevron-left'
import ChevronRight from '@/components/icons/chevron-right'
import DownloadWithBasket from '@/components/icons/download-with-basket'
import EyeIcon from '@/components/icons/eye'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { getCurrencySymbol } from '@/helpers/currency'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import dayjs from '@/lib/dayjs'
import { cn } from '@/lib/utils'
import apiFetch from '@/store/helpers/api-fetch'
import Link from 'next/link'
import { useRouter } from 'nextjs-toploader/app'
import { useMemo, useRef } from 'react'
import useSWR from 'swr'
import LiveryOverviewRecentSalesCard from './recent-sales-card'
import LiveryOverviewTopLiveriesWeek from './top-liveries-week'

export default function Overview({ session, games = [] }) {
  const { data: user, isLoading: userLoading } = useCurrentUser()

  const swrLookup = {
    url: `user/${user?.id}/profile/livery/stats`,
  }

  const { data: liveryStats, isLoading: liveryStatsLoading } = useSWR(
    user?.id ? swrLookup : null,
    apiFetch,
  )

  const { data: trendingLiveries, isLoading: trendingLiveriesLoading } = useSWR(
    {
      url: 'livery-design/trending',
      searchParams: {
        trend: 'downloads',
        limit: 10,
      },
    },
    apiFetch,
  )

  const { data: transactions, isLoading: transactionsLoading } = useSWR(
    {
      url: session?.user?.id
        ? `user/${session.user.id}/livery-transaction-log`
        : null,
      searchParams: {
        seller: true,
        include: {
          liveryDesign: {
            include: {
              car: {
                include: {
                  carClass: true,
                },
              },
              platforms: {
                include: {
                  platform: true,
                },
              },
              game: true,
            },
          },
        },
        orderBy: {
          createTs: 'desc',
        },
      },
    },
    apiFetch,
  )

  const lastWeeksSoldDesigns = useMemo(() => {
    if (!transactions?.length) return []

    const lastWeekTransactions = transactions.filter((t) =>
      dayjs(t.createTs).isAfter(dayjs().subtract(7, 'days')),
    )

    if (!lastWeekTransactions.length) return []

    const designCountMap = lastWeekTransactions.reduce((countMap, t) => {
      const designId = t.liveryDesign.id
      countMap[designId] = (countMap[designId] || 0) + 1
      return countMap
    }, {})

    const sortedDesignIds = Object.entries(designCountMap)
      .sort(([, countA], [, countB]) => countB - countA)
      .map(([key]) => key)

    const uniqueSortedLiveryDesigns = sortedDesignIds.map((id) => {
      const designToReturn = lastWeekTransactions.find(
        (t) => t.liveryDesign.id === id,
      )?.liveryDesign

      if (!designToReturn) return null

      return {
        ...designToReturn,
        soldLastWeek: designCountMap[id],
      }
    })

    return uniqueSortedLiveryDesigns.filter(Boolean)
  }, [transactions])

  const downloads = liveryStats?.liveryDesigns?.downloads?.total ?? 0
  const revenue = liveryStats?.liveryDesigns?.revenue?.total ?? 0

  const containerRef = useRef()
  const router = useRouter()

  const handleScroll = (direction) => {
    const scrollAmount = containerRef.current.childNodes[0].offsetWidth
    const newScrollPosition =
      direction === 'forward'
        ? containerRef.current.scrollLeft + scrollAmount
        : containerRef.current.scrollLeft - scrollAmount

    containerRef.current.scrollLeft = newScrollPosition
  }

  function handleUploadLivery() {
    router.push('/profile-manager/liveries/upload-livery')
  }

  const latestPaidSaleDate = useMemo(() => {
    if (!transactions?.length) return '-'

    const latestPaidTransaction = transactions.find((transaction) => {
      const salePrice = Number(transaction.salePrice)
      return Number.isFinite(salePrice) && salePrice > 0
    })

    if (!latestPaidTransaction) return '-'

    return dayjs(latestPaidTransaction.createTs).format('MMM D, YYYY')
  }, [transactions])

  return (
    <div className="col-span-full grid h-full w-full grid-cols-12 items-center justify-center bg-neutral-blue-900 font-inter">
      <div className="col-span-full bg-neutral-blue-900">
        <div className="flex items-center justify-between gap-2 align-middle">
          <h4 className="text-h-4 font-semibold">Overview</h4>
          <Button
            variant="outline"
            className="border-neutral-blue-700 bg-transparent px-5 py-3 text-p-1 font-bold"
            onClick={handleUploadLivery}
          >
            Upload Livery
          </Button>
        </div>
        {userLoading ? (
          <Skeleton className="mt-4 flex min-h-[45vh] w-full" />
        ) : liveryStats?.liveryDesigns?.counts?.total > 0 ? (
          <div>
            <div className="mt-4 grid grid-cols-2 gap-2 tablet:grid-cols-4 tablet:gap-4">
              <div className="flex flex-col justify-end rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 px-4 py-4">
                <p className="mb-auto text-p-2 font-semibold text-success-500 mobile:text-[18px] tablet:text-[20px] desktop-xs:text-[26px]">
                  {liveryStatsLoading ? (
                    <Loader size="sm pt-[6px]!" />
                  ) : (
                    downloads
                  )}
                </p>
                <p className="mt-2 text-p-1 text-neutral-blue-400">
                  Total Downloads
                </p>
              </div>
              <div className="rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 px-4 py-4">
                <p className="text-p-2 font-semibold text-success-500 mobile:text-[18px] tablet:text-[20px] desktop-xs:text-[26px]">
                  {liveryStats?.liveryDesigns?.counts?.total}
                </p>
                <p className="mt-2 text-p-1 text-neutral-blue-400">Designs</p>
              </div>
              <div className="rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 px-4 py-4">
                <p className="text-p-2 font-semibold text-success-500 mobile:text-[18px] tablet:text-[20px] desktop-xs:text-[26px]">
                  {latestPaidSaleDate}
                </p>
                <p className="mt-2 text-p-1 text-neutral-blue-400">
                  Latest Sale
                </p>
              </div>
              <div className="items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 px-4 py-4">
                <div className="w-fit text-p-2 font-semibold text-success-500 mobile:text-[18px] tablet:mx-auto tablet:text-[20px] desktop-xs:text-[26px]">
                  {transactions?.length > 0 ? (
                    <p>
                      {getCurrencySymbol(transactions[0].saleCurrencyId)}
                      <span className="ml-[2px]">
                        {Number(revenue.toPrecision(4))}
                      </span>
                    </p>
                  ) : (
                    <p>0</p>
                  )}
                </div>
                <p className="mt-2 w-fit text-p-1 text-neutral-blue-400 tablet:mx-auto tablet:text-center">
                  Total revenue earned
                </p>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1 gap-4 desktop-sm:grid-cols-2">
              <div className="items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 px-2 py-4 tablet:px-4">
                <div className="flex justify-between align-middle">
                  <h6 className="text-h-6 font-semibold">
                    My top liveries this week
                  </h6>
                  <Link
                    className="hidden items-center align-middle tablet:flex"
                    href="/profile-manager/liveries/my-liveries"
                  >
                    <span className="cursor-pointer font-semibold underline-offset-2 hover:underline">
                      My Liveries
                    </span>
                    <ChevronRight className="ml-1 h-4 w-4 stroke-white stroke-[3px]" />
                  </Link>
                </div>

                <div className="h-full w-full">
                  {transactionsLoading ? (
                    <Skeleton className="mt-4 h-[200px] w-full" />
                  ) : lastWeeksSoldDesigns.length > 0 ? (
                    lastWeeksSoldDesigns.map((liveryDesign, index) => (
                      <LiveryOverviewTopLiveriesWeek
                        liveryDesign={liveryDesign}
                        games={games}
                        index={index}
                        countSoldLastWeek={liveryDesign.soldLastWeek}
                        key={index}
                      />
                    ))
                  ) : (
                    <div className="mx-auto flex h-full w-fit flex-col py-6">
                      <h6 className="mb-2 mt-auto text-h-6 text-white">
                        No sales this week
                      </h6>
                      <button
                        className="rounded-sm! hover:border-neutral-blue-700! inline-flex items-center justify-center gap-2 rounded-sm border border-neutral-blue-900 border-transparent bg-neutral-blue-900 px-4 py-2 text-center text-p-1 text-sm font-medium normal-case text-shades-0 transition-colors duration-200 hover:bg-neutral-blue-700 hover:bg-neutral-blue-900 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => handleUploadLivery()}
                      >
                        Upload Livery
                      </button>
                      <div className="mb-auto select-none text-neutral-blue-800">
                        ‎
                      </div>
                    </div>
                  )}
                </div>

                <button className="rounded-sm! hover:border-neutral-blue-100! hover:bg-neutral-blue-800! mx-4 mt-4 flex inline-flex w-[calc(100%-2rem)] items-center justify-center gap-2 rounded-sm border border-neutral-blue-500 border-transparent px-4 py-2 text-center text-sm font-medium normal-case text-shades-0 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60 desktop-sm:hidden">
                  View all liveries
                  <ChevronRight className="ml-1 h-4 w-4 stroke-white stroke-[2px]" />
                </button>
              </div>

              {/* recent downloads */}
              <div className="items-center rounded-sm border border-neutral-blue-700 bg-neutral-blue-800 px-2 py-4 desktop-xs:px-4">
                <div className="flex justify-between align-middle">
                  <h6 className="ml-4 text-h-6 font-semibold desktop-sm:ml-0">
                    Most recent sales
                  </h6>
                  <Link
                    href="/profile-manager/liveries/my-sales"
                    className="hidden items-center align-middle tablet:flex"
                  >
                    <span className="cursor-pointer font-semibold underline-offset-2 hover:underline">
                      My Sales
                    </span>
                    <ChevronRight className="ml-1 h-4 w-4 stroke-white stroke-[3px]" />
                  </Link>
                </div>
                {transactionsLoading ? (
                  <Skeleton className="mt-4 h-[200px] w-full" />
                ) : (
                  <div className="relative w-full">
                    {transactions?.slice(0, 4).map((transaction, index) => (
                      <LiveryOverviewRecentSalesCard
                        key={transaction.id}
                        transaction={transaction}
                        index={index}
                      />
                    ))}
                    {transactions && transactions?.length === 0 && (
                      <div className="mx-auto mt-4 flex h-full w-fit flex-col py-6">
                        <div className="mb-2 mt-auto text-h-6 text-white">
                          No sales this week
                        </div>
                        <button
                          className="rounded-sm! hover:border-neutral-blue-700! inline-flex items-center justify-center gap-2 rounded-sm border border-neutral-blue-900 border-transparent bg-neutral-blue-900 px-4 py-2 text-center text-p-1 text-sm font-medium normal-case text-shades-0 transition-colors duration-200 hover:bg-neutral-blue-700 hover:bg-neutral-blue-900 disabled:cursor-not-allowed disabled:opacity-60"
                          onClick={() => handleUploadLivery()}
                        >
                          Upload Livery
                        </button>
                        <div className="mb-auto select-none text-neutral-blue-800">
                          ‎
                        </div>
                      </div>
                    )}
                  </div>
                )}
                <button className="rounded-sm! hover:border-neutral-blue-100! hover:bg-neutral-blue-800! mx-4 mt-4 flex inline-flex w-[calc(100%-2rem)] items-center justify-center gap-2 rounded-sm border border-neutral-blue-500 border-transparent px-4 py-2 text-center text-sm font-medium normal-case text-shades-0 transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60 desktop-sm:hidden">
                  Most recent downloads
                  <ChevronRight className="ml-1 h-4 w-4 stroke-white stroke-[2px]" />
                </button>
              </div>
            </div>

            {/* divider */}
            <div className="divider" />

            {/* trending liveries section */}
            {trendingLiveriesLoading ? (
              <Loader size="md" className="mx-auto w-full max-w-7xl" />
            ) : (
              <>
                <div className="mt-4 flex items-center justify-between">
                  <h6 className="font-inter text-h-6 font-semibold tablet:text-h-5">
                    Trending designs in the community
                  </h6>
                  <div className="hidden gap-4 tablet:flex">
                    <button
                      className="relative flex h-10 w-10 items-center"
                      onClick={() => handleScroll('back')}
                    >
                      <div className="absolute z-0 h-full w-full rounded-full bg-neutral-blue-700 p-4"></div>
                      <ChevronLeft className="stroke-3 z-20 mx-auto h-4 w-4 cursor-pointer stroke-shades-0" />
                    </button>

                    <button
                      className="relative flex h-10 w-10 items-center"
                      onClick={() => handleScroll('forward')}
                    >
                      <div className="absolute z-0 h-full w-full rounded-full bg-neutral-blue-700 p-4"></div>
                      <ChevronRight className="stroke-3 z-20 mx-auto h-4 w-4 cursor-pointer stroke-shades-0" />
                    </button>
                  </div>
                </div>

                <div
                  ref={containerRef}
                  className={cn(
                    'no-scrollbar mb-4 mt-4 grid h-auto w-full snap-x snap-proximity auto-cols-[minmax(calc(100%),100%)] grid-flow-col grid-rows-1 gap-1 space-x-2 overflow-x-auto scroll-smooth pr-6 motion-reduce:scroll-auto desktop-md:auto-cols-[minmax(calc(50%-8px),50%)] desktop-md:gap-4 desktop-md:space-x-0 desktop-md:pr-0 [&>*]:snap-start',
                  )}
                >
                  {trendingLiveries?.map((livery, index) => {
                    let plat = null

                    if (livery.platforms?.length > 0) {
                      if (livery.platforms.length > 1) {
                        plat = 'Multi Platform'
                      } else {
                        plat = livery.platforms[0].nicename
                      }
                    }

                    return (
                      <div
                        key={index}
                        id={index}
                        className="tablet bg-linear-to-b tablet:snap-start! tablet:bg-linear-to-br box-border flex h-full flex-col gap-1 rounded-sm border border-neutral-blue-700 from-[#2a3d53] to-[#08111B] p-4 pb-1 tablet:mr-0 tablet:flex-row tablet:gap-4 tablet:rounded-lg tablet:from-neutral-blue-800 tablet:via-neutral-blue-700 tablet:to-neutral-blue-800 tablet:pb-4"
                      >
                        {/* img */}
                        <div className="w-full tablet:w-[40%]">
                          <Link
                            href={`/sim-racing-liveries/${livery.id}`}
                            className="h-min-full relative flex aspect-video h-full w-full overflow-hidden rounded-sm tablet:rounded-lg"
                          >
                            <ImageWithFallback
                              fill
                              fallbackUrl="/img/default/header.webp"
                              alt="Event Header Image"
                              className="relative rounded-sm object-cover tablet:rounded-lg"
                              src={
                                livery.files?.find(
                                  (file) =>
                                    file.usageHint === 'user-livery-image',
                                )?.fileUrl
                              }
                            />
                          </Link>
                        </div>
                        {/* text */}
                        <div className="mb-2 flex w-full flex-col px-4 tablet:w-[60%] tablet:px-0">
                          <div className="mt-6 flex">
                            <div className="items-center align-middle text-p-1 font-semibold text-neutral-blue-500">
                              <DownloadWithBasket
                                stroke="neutral-blue-500"
                                className="mb-[4px] mr-[5px] inline-block h-4 w-4 rotate-90 stroke-neutral-blue-500 stroke-[2px]"
                              />
                              {livery._stats.downloads.total}
                            </div>
                            <div className="ml-6 items-center align-middle text-p-1 font-semibold text-neutral-blue-500">
                              <EyeIcon className="mb-[2px] mr-[5px] inline-block h-4 w-4 stroke-neutral-blue-500 stroke-[2px]" />

                              {livery._stats.views.total}
                            </div>
                          </div>
                          <h6 className="truncate-2 mt-3 text-h-6 font-semibold text-shades-0 tablet:text-h-5">
                            {livery.name}
                          </h6>
                          <div className="font-normal text-neutral-blue-500">
                            <div className="mt-0 flex font-inter text-[14px] text-neutral-blue-500 tablet:text-[14px]">
                              {plat}
                              {plat && livery?.game?.nicename && ' • '}
                              {livery?.game?.nicename && (
                                <span className="hidden tablet:contents">
                                  {livery.game.nicename}
                                </span>
                              )}
                            </div>
                            {livery.car?.name && livery.car.name}
                          </div>
                          {/* sale price and price */}
                          <div className="my-auto flex">
                            <div className="flex items-center align-middle font-bold">
                              {livery?.salePrice ? (
                                <>
                                  <div className="text-p-2 text-error-500 tablet:text-p-3">
                                    {livery.currency.symbol} {livery.salePrice}
                                  </div>
                                  <div className="ml-3 text-p-1 text-neutral-blue-500 line-through tablet:text-p-2">
                                    {livery.currency.symbol} {livery.price}
                                  </div>
                                </>
                              ) : (
                                <div className="text-p-2 text-white">
                                  {livery.price > 0
                                    ? `${livery.currency.symbol} ${livery.price}`
                                    : 'FREE'}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="truncate-1 mb-4 mt-auto text-p-1 font-normal text-neutral-blue-500 tablet:text-p-2">
                            Designed by{' '}
                            <span className="font-inter text-shades-0 underline underline-offset-2">
                              {livery.user?.username ||
                                livery.user?.discordUsername ||
                                'Unknown User'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            )}
          </div>
        ) : (
          <div className="mt-7 flex min-h-[45vh] w-full flex-col items-center justify-center gap-6 rounded-lg border border-neutral-blue-700 bg-neutral-blue-800 px-6 desktop-xs:rounded-sm">
            <div className="text-center text-h-5 font-semibold">
              Upload your first Livery to start earning!
            </div>
            <p className="flex flex-col text-center text-p-3 text-neutral-blue-500">
              <span>Upload a Livery to Grid Finder to sell or give to the</span>
              <span>Community for free. We support all major game titles.</span>
            </p>
            <Button
              variant="outline"
              className="border-neutral-blue-700 bg-transparent px-8 py-4 text-p-1 font-bold"
              onClick={handleUploadLivery}
            >
              Upload Livery
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
